import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// Ilustraciones SVG propias (~5 KB cada una)
import imgSeguridad from '@/assets/servicios/seguridad.svg';
import imgElectrico from '@/assets/servicios/electrico.svg';

const SERVICES = [
  {
    id: 'montaje',
    icon: 'ri-sun-line',
    tag: 'Energía solar',
    title: 'Montaje de paneles solares',
    description: 'Instalación profesional de sistemas fotovoltaicos para hogares y negocios. Diseño personalizado, cableado y puesta en marcha.',
    features: ['Diseño del sistema', 'Instalación completa', 'Config. de inversores', 'Puesta en marcha'],
    image: 'https://readdy.ai/api/search-image?query=professional%20technician%20installing%20solar%20panels%20on%20rooftop%20sunny%20day%20worker%20safety%20equipment%20photovoltaic%20system%20installation%20blue%20sky%20background%20realistic%20photography%20warm%20light&width=700&height=900&seq=service-solar-montaje-v2&orientation=portrait',
    accent: '#4bbfdf',
    accentBg: 'rgba(75,191,223,0.18)',
    accentBorder: 'rgba(75,191,223,0.30)',
    comingSoon: false,
    link: '/servicios/paneles',
  },
  {
    id: 'herreria',
    icon: 'ri-building-4-line',
    tag: 'Herrería',
    title: 'Terrazas para paneles solares',
    description: 'Fabricamos estructuras metálicas a medida para tu patio o azotea. Una terraza sólida y estética donde montar tus paneles.',
    features: ['Diseño a medida', 'Acero galvanizado', 'Instalación incluida', 'Optimizado para paneles'],
    image: 'https://readdy.ai/api/search-image?query=custom%20metal%20pergola%20terrace%20structure%20backyard%20solar%20panels%20mounted%20top%20modern%20steel%20construction%20sunny%20outdoor%20patio%20warm%20sunlight%20green%20garden%20professional%20photography&width=700&height=900&seq=service-herreria-v2&orientation=portrait',
    accent: '#1e2d4a',
    accentBg: 'rgba(30,45,74,0.18)',
    accentBorder: 'rgba(30,45,74,0.30)',
    comingSoon: false,
    link: '/servicios/terrazas',
  },
  {
    id: 'seguridad',
    icon: 'ri-cctv-line',
    tag: 'Seguridad',
    title: 'Cámaras y sistemas de seguridad',
    description: 'Instalamos cámaras de seguridad para casas y negocios. Ves todo desde tu celular y siguen grabando durante los apagones.',
    features: ['Cámaras HD', 'Vista en el celular', 'Grabación 24/7', 'Funciona en apagones'],
    image: imgSeguridad,
    accent: '#f59e0b',
    accentBg: 'rgba(245,158,11,0.18)',
    accentBorder: 'rgba(245,158,11,0.30)',
    comingSoon: false,
    link: '/servicios/seguridad',
  },
  {
    id: 'electrico',
    icon: 'ri-flashlight-line',
    tag: 'Electricidad',
    title: 'Diseño eléctrico residencial desde cero',
    description: 'Diseñamos e instalamos la electricidad de tu casa: cálculo de cargas, circuitos, pizarra y cableado, lista para energía solar.',
    features: ['Cálculo de cargas', 'Plano de circuitos', 'Pizarra y breakers', 'Lista para solar'],
    image: imgElectrico,
    accent: '#8b5cf6',
    accentBg: 'rgba(139,92,246,0.18)',
    accentBorder: 'rgba(139,92,246,0.30)',
    comingSoon: false,
    link: '/servicios/electrico',
  },
  {
    id: 'domotica',
    icon: 'ri-home-wifi-line',
    tag: 'Domótica',
    title: 'Instalaciones inteligentes',
    description: 'Automatización del hogar: control de iluminación, climatización, seguridad y electrodomésticos desde tu teléfono.',
    features: ['Control por app', 'Automatización de luces', 'Seguridad inteligente', 'Integración total'],
    image: 'https://readdy.ai/api/search-image?query=modern%20smart%20home%20automation%20interior%20living%20room%20automated%20lighting%20technology%20devices%20connected%20minimal%20aesthetic%20professional%20photography%20evening%20ambiance&width=700&height=900&seq=service-domotica-v2&orientation=portrait',
    accent: '#10b981',
    accentBg: 'rgba(16,185,129,0.18)',
    accentBorder: 'rgba(16,185,129,0.30)',
    comingSoon: true,
    link: '',
  },
];

/** Fondo de respaldo: degradado con el icono, por si la foto no carga. */
function FondoServicio({ service }: { service: typeof SERVICES[0] }) {
  return (
    <div className="absolute inset-0" style={{ background: `linear-gradient(160deg, ${service.accent}55 0%, #0d1322 70%)` }}>
      <i className={`${service.icon} absolute`} style={{ top: '12%', right: '-1.5rem', fontSize: '11rem', color: `${service.accent}30`, lineHeight: 1 }} aria-hidden="true" />
    </div>
  );
}

// ── Coming Soon Overlay ──────────────────────────────────────────────────────
function ComingSoonOverlay({ onClose, accent }: { onClose: () => void; accent: string }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full sm:w-auto sm:min-w-[340px] sm:max-w-sm mx-4 rounded-t-3xl sm:rounded-3xl overflow-hidden"
        style={{ background: '#111' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}88)` }} />
        <div className="px-7 pt-8 pb-10 flex flex-col items-center text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl mb-5"
            style={{ background: `${accent}18`, border: `1px solid ${accent}30` }}>
            <i className="ri-home-wifi-line text-3xl" style={{ color: accent }} />
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest mb-4"
            style={{ background: `${accent}15`, border: `1px solid ${accent}25`, color: accent }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accent }} />
            Próximamente
          </span>
          <h3 className="font-black text-white text-xl leading-tight mb-2">Instalaciones inteligentes</h3>
          <p className="text-neutral-400 text-sm leading-relaxed mb-6 max-w-[260px]">
            Estamos preparando este servicio para ofrecerte la mejor experiencia en domótica y automatización del hogar.
          </p>
          <div className="w-full mb-6">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] text-neutral-500 font-semibold uppercase tracking-wider">En desarrollo</span>
              <span className="text-[10px] font-black" style={{ color: accent }}>65%</span>
            </div>
            <div className="h-1.5 rounded-full w-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <div className="h-full rounded-full" style={{ width: '65%', background: `linear-gradient(90deg, ${accent}88, ${accent})` }} />
            </div>
          </div>
          <Link to="/contact" onClick={onClose}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-[13px] cursor-pointer whitespace-nowrap mb-3"
            style={{ background: accent, color: '#000' }}>
            <i className="ri-whatsapp-line" />
            Notificarme cuando esté listo
          </Link>
          <button onClick={onClose}
            className="flex items-center gap-1.5 text-neutral-500 text-sm font-medium cursor-pointer hover:text-neutral-300 transition-colors">
            <i className="ri-arrow-left-line text-sm" />
            Volver
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ServicesSection() {
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleCardClick = (service: typeof SERVICES[0]) => {
    if (service.comingSoon) {
      setComingSoonOpen(true);
    } else if (service.link) {
      navigate(service.link);
    }
  };

  return (
    <section id="servicios" className="py-14 md:py-20" style={{ background: '#111111' }}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className="mb-8 md:mb-12">
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: '#4bbfdf' }}>Lo que hacemos</span>
          <h2 className="font-bold text-2xl md:text-3xl text-white leading-tight">Servicios especializados</h2>
          <p className="text-neutral-400 text-sm md:text-base mt-2 max-w-xl">
            Más allá de los productos, ofrecemos instalación y configuración profesional para que todo funcione perfecto.
          </p>
        </div>

        {/* ── MOBILE ── */}
        <div className="md:hidden">
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', paddingRight: '24px' }}
            onScroll={(e) => {
              const el = e.currentTarget;
              const cardWidth = el.scrollWidth / SERVICES.length;
              const idx = Math.round(el.scrollLeft / cardWidth);
              setActiveIndex(Math.min(idx, SERVICES.length - 1));
            }}
          >
            {SERVICES.map((service) => (
              <div key={service.id}
                className="shrink-0 relative rounded-3xl overflow-hidden flex flex-col justify-end active:scale-[0.98] transition-transform duration-150 cursor-pointer"
                style={{ width: '72vw', maxWidth: '280px', minWidth: '240px', height: '420px', scrollSnapAlign: 'start' }}
                onClick={() => handleCardClick(service)}
              >
                <FondoServicio service={service} />
                <img src={service.image} alt={service.title} loading="lazy" decoding="async"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  style={{ filter: service.comingSoon ? 'brightness(0.55) saturate(0.6)' : 'none' }} />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.10) 100%)' }} />
                <div className="absolute top-0 inset-x-0 h-24 pointer-events-none" style={{ background: `linear-gradient(to bottom, ${service.accentBg} 0%, transparent 100%)` }} />

                {service.comingSoon && (
                  <div className="absolute top-4 right-4 z-20">
                    <span className="flex items-center gap-1.5 text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest"
                      style={{ background: `${service.accent}20`, border: `1px solid ${service.accent}40`, color: service.accent, backdropFilter: 'blur(8px)' }}>
                      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: service.accent }} />
                      Próximamente
                    </span>
                  </div>
                )}

                <div className="relative z-10 flex flex-col p-5">
                  <div className="mb-3">
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest"
                      style={{ background: service.accentBg, border: `1px solid ${service.accentBorder}`, color: service.accent }}>
                      <i className={`${service.icon} text-xs`} />
                      {service.tag}
                    </span>
                  </div>
                  <h3 className="font-black text-[18px] leading-tight text-white mb-2">{service.title}</h3>
                  <p className="text-white/60 text-[12px] leading-relaxed mb-4 line-clamp-2">{service.description}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {service.features.map((f) => (
                      <span key={f} className="text-[10px] font-semibold px-2.5 py-1 rounded-full text-white/80" style={{ background: 'rgba(255,255,255,0.10)' }}>{f}</span>
                    ))}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCardClick(service); }}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-[13px] font-bold cursor-pointer whitespace-nowrap"
                    style={{ background: service.accent, color: '#080808' }}
                  >
                    {service.comingSoon ? <><i className="ri-time-line text-sm" />Ver más info</> : <>Ver servicio<i className="ri-arrow-right-line text-sm" /></>}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-2 mt-3" aria-hidden="true">
            {SERVICES.map((s, i) => (
              <span
                key={s.id}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === activeIndex ? '20px' : '6px',
                  height: '6px',
                  background: i === activeIndex ? '#a3a3a3' : '#d4d4d4',
                }}
              />
            ))}
          </div>
        </div>

        {/* ── DESKTOP ── */}
        <div className="hidden md:grid grid-cols-3 gap-6">
          {SERVICES.map((service) => (
            <div key={service.id}
              className="group relative rounded-3xl overflow-hidden flex flex-col justify-end transition-transform duration-300 hover:-translate-y-1 cursor-pointer"
              style={{ height: '480px' }}
              onClick={() => handleCardClick(service)}
            >
              <FondoServicio service={service} />
                <img src={service.image} alt={service.title} loading="lazy" decoding="async"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-600 group-hover:scale-[1.04]"
                style={{ filter: service.comingSoon ? 'brightness(0.50) saturate(0.5)' : 'none' }} />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.50) 50%, rgba(0,0,0,0.08) 100%)' }} />
              <div className="absolute top-0 inset-x-0 h-28 pointer-events-none" style={{ background: `linear-gradient(to bottom, ${service.accentBg} 0%, transparent 100%)` }} />

              {service.comingSoon && (
                <div className="absolute top-5 right-5 z-20">
                  <span className="flex items-center gap-1.5 text-[11px] font-black px-3.5 py-2 rounded-full uppercase tracking-widest"
                    style={{ background: `${service.accent}18`, border: `1px solid ${service.accent}35`, color: service.accent, backdropFilter: 'blur(10px)' }}>
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: service.accent }} />
                    Próximamente
                  </span>
                </div>
              )}

              <div className="relative z-10 flex flex-col p-6">
                <div className="mb-3">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest"
                    style={{ background: service.accentBg, border: `1px solid ${service.accentBorder}`, color: service.accent }}>
                    <i className={`${service.icon} text-xs`} />
                    {service.tag}
                  </span>
                </div>
                <h3 className="font-black text-xl leading-tight text-white mb-2">{service.title}</h3>
                <p className="text-white/60 text-[13px] leading-relaxed mb-4 line-clamp-3">{service.description}</p>
                <div className="flex flex-wrap gap-1.5 mb-5">
                  {service.features.map((f) => (
                    <span key={f} className="text-[10px] font-semibold px-2.5 py-1 rounded-full text-white/75" style={{ background: 'rgba(255,255,255,0.10)' }}>{f}</span>
                  ))}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleCardClick(service); }}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-[13px] font-bold cursor-pointer whitespace-nowrap opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300"
                  style={{ background: service.accent, color: '#000' }}
                >
                  {service.comingSoon ? <><i className="ri-time-line" />Ver más info</> : <>Ver servicio<i className="ri-arrow-right-line text-sm" /></>}
                </button>
                {!service.comingSoon && (
                  <span className="flex items-center gap-1.5 text-[12px] font-semibold mt-2 group-hover:opacity-0 transition-opacity duration-200 cursor-pointer" style={{ color: service.accent }}>
                    Ver servicio <i className="ri-arrow-right-line text-xs" />
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {comingSoonOpen && (
        <ComingSoonOverlay onClose={() => setComingSoonOpen(false)} accent={SERVICES.find((s) => s.comingSoon)?.accent ?? '#10b981'} />
      )}
    </section>
  );
}
